import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import '../../pages/SurveyStart.scss';
import { useDispatch } from 'react-redux';
import { submitPreliminarySurveyAnswerAction, getPreliminarySurveyResultsAction } from '../../redux/attemptSurvey/attemptSurvey.actions';
import { shouldTreatPreliminarySubmitAsFail, getPreliminaryFailMessage } from '../../helper/preliminarySurvey';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';

const SurveyPrimaryQuestion = ({
  preliminaryQuestionId,
  comment,
  setComment,
  survey,
  isPreliminary,
  setSelectedOption,
  setSelectedOptions,
  selectedOptions,
  handleCheckboxChange,
  questionList,
  selectedOption,
  questionIndex,
  question,
  mandatory,
  options,
  points,
  maxTime,
  skipWelcomeModal = false,
  onChange,
  onNext,
  onFail
}) => {
  const dispatch = useDispatch();
  const totalSeconds = Number(maxTime) || 0;
  const [timeLeft, setTimeLeft] = useState(totalSeconds);
  const timerLabelRef = useRef(null);
  const intervalRef = useRef(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(true);
  const [timerActive, setTimerActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const displaySeconds = Math.max(0, timeLeft);

  const language = localStorage.getItem('language') || 'Italian';

  const texts = {
    Italian: {
      selectOptionError: "Seleziona un'opzione per questa domanda preliminare",
      timeExpiredTitle: "Tempo Scaduto!",
      timeExpiredBody: "La tua risposta non è stata salvata a causa della scadenza del tempo.",
      welcomeTitle: "Benvenuto alla Domanda Preliminare",
      welcomeBody: `Questa è una domanda a tempo limitato. Hai ${maxTime} secondi per rispondere. Per favore, cerca di completarla il più velocemente possibile. Nota: Devi rispondere correttamente per procedere con il sondaggio.`,
      readyButton: "Sono Pronto",
      nextButtonLoading: "Invio in corso...",
      nextButton: "Salva e Avanti",
      preliminaryQuestion: "Domanda Preliminare"
    },
    English: {
      selectOptionError: "Please select an option for this preliminary question",
      timeExpiredTitle: "Time Expired!",
      timeExpiredBody: "Your response was not saved due to time expiration.",
      welcomeTitle: "Welcome to the Preliminary Question",
      welcomeBody: `This is a timed question. You have ${maxTime} seconds to answer. Please try to complete it as quickly as possible. Note: You must answer correctly to proceed with the survey.`,
      readyButton: "I'm Ready",
      nextButtonLoading: "Submitting...",
      nextButton: "Save and Next",
      preliminaryQuestion: "Preliminary Question"
    }
  };

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setTimeLeft(totalSeconds);
    setSelectedAnswer(null);
    setSelectedOption(null);
    setShowWelcomeModal(!skipWelcomeModal);
    setTimerActive(skipWelcomeModal);
    setShowModal(false);
    setIsSubmitting(false);
  }, [questionIndex, skipWelcomeModal, totalSeconds]);

  useEffect(() => {
    if (!timerActive) {
      setTimeLeft(totalSeconds);
    }
  }, [totalSeconds, timerActive]);

  useLayoutEffect(() => {
    if (timerLabelRef.current) {
      timerLabelRef.current.textContent = `${displaySeconds}s`;
    }
  }, [displaySeconds]);

  useEffect(() => {
    if (!timerActive || totalSeconds <= 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          setShowModal(true);
          setTimerActive(false);
          return 0;
        }
        return next;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [timerActive]);

  const handleOptionSelect = (option) => {
    const id = (option && typeof option === 'object' && option._id) ? option._id : option;
    setSelectedAnswer(id);
    setSelectedOption(id);
  };

  const handleNext = async () => {
    if (isSubmitting) {
      return;
    }

    if (!selectedAnswer && mandatory) {
      toast.error(language === 'Italian' ? texts[language].selectOptionError : texts[language].selectOptionError);
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await dispatch(submitPreliminarySurveyAnswerAction(
        survey,
        preliminaryQuestionId,
        selectedAnswer
      ));

      if (shouldTreatPreliminarySubmitAsFail(result)) {
        const apiMessage = getPreliminaryFailMessage(result);
        onFail?.(apiMessage);
        return;
      }

      if (result?.success) {
        onNext();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTimeExpiredClose = () => {
    setShowModal(false);
    setTimerActive(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    navigate('/sondaggi');
  };

  const handleWelcomeModalClose = () => {
    setShowWelcomeModal(false);
    setTimeLeft(totalSeconds);
    setTimerActive(true);
  };

  return (
    <div>
      <Modal show={showModal} onHide={handleTimeExpiredClose} centered backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>{language === 'Italian' ? texts[language].timeExpiredTitle : texts[language].timeExpiredTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{language === 'Italian' ? texts[language].timeExpiredBody : texts[language].timeExpiredBody}</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleTimeExpiredClose}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showWelcomeModal} onHide={() => {}} centered backdrop="static" keyboard={false}>
        <Modal.Header>
          <Modal.Title>{language === 'Italian' ? texts[language].welcomeTitle : texts[language].welcomeTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{language === 'Italian' ? texts[language].welcomeBody : texts[language].welcomeBody}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleWelcomeModalClose}>
            {language === 'Italian' ? texts[language].readyButton : texts[language].readyButton}
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="title">
        <button>
          {language === 'Italian' ? texts[language].preliminaryQuestion : texts[language].preliminaryQuestion}
        </button>
      </div>
      <div className="question_card">
        <div className="question_count">
          <div className="counts"><span>{questionIndex + 1}</span>/{questionList.length}</div>
          <div className="timer">
            <div className="timer-spinner" aria-hidden="true" />
            <span
              ref={timerLabelRef}
              className="seconds"
              aria-live="polite"
              aria-atomic="true"
            >
              {displaySeconds}s
            </span>
          </div>
        </div>
        <div className="question">
          {questionIndex + 1}. {question}
        </div>
        <div className="option_wrapper">
          {options?.map((option, index) => (
            <label key={index} className="option" htmlFor={`${questionIndex}-${index}`}>
              <input
                type="radio"
                name={`question-${questionIndex}`}
                id={`${questionIndex}-${index}`}
                checked={String(selectedAnswer || '') === String((option && typeof option === 'object' ? option._id : option) || '')}
                onChange={() => handleOptionSelect(option)}
              />
              <div className="text">
                {option?.option || option}
              </div>
              <div className="right">
                <div className="tick"></div>
              </div>
            </label>
          ))}
        </div>
        <div className="action center" style={{ cursor: 'pointer' }}>
          <button
            type="button"
            className="next_button"
            disabled={isSubmitting}
            onClick={handleNext}
          >
            {isSubmitting ? (language === 'Italian' ? texts[language].nextButtonLoading : texts[language].nextButtonLoading) : (language === 'Italian' ? texts[language].nextButton : texts[language].nextButton)}
          </button>
        </div>
      </div>
    </div>
  )
}

export default SurveyPrimaryQuestion
